// ---------------------------------------------------------------------------
// AN-024 — Tenant POV Baseline + Snapshot projection tables
// ---------------------------------------------------------------------------
// Companion projection tables for the AN-024 auto-capture lane (catalog in
// `an-024-pov-baseline-events.ts`). Two tables, one purpose:
//
//   - `tenant_pov_baselines`   — singleton row per tenant. The pre-platform
//     reference snapshot captured at onboarding completion. Read by the
//     POV report generator to compute "improvement vs. baseline".
//   - `tenant_pov_snapshots`   — append-only history written by the weekly
//     cron. Lets the POV report render a baseline-anchored time series
//     rather than only a point-in-time delta.
//
// Per CLAUDE.md Governance Rule #1 (CQRS only): these projection rows MUST
// be materialised FROM the immutable events catalogued in
// `an-024-pov-baseline-events.ts` — never via direct mutation by route
// handlers. The future onboarding-completion hook emits
// `analytics.pov_baseline_captured@v1`, the weekly cron emits
// `analytics.pov_snapshot_taken@v1`, and a materialiser writes these rows.
//
// Per Governance Rule #4 (First-Seen Immutability): the baseline row, once
// captured, is NEVER overwritten — UNIQUE on `tenant_id` is the database-
// level enforcement.
// ---------------------------------------------------------------------------

import {
  pgTable,
  uuid,
  integer,
  bigint,
  timestamp,
  index,
  uniqueIndex,
} from "drizzle-orm/pg-core";

const tz = { withTimezone: true } as const;

// ---------------------------------------------------------------------------
// 1. tenant_pov_baselines — singleton per tenant (UNIQUE on tenant_id)
// ---------------------------------------------------------------------------
// Captured at onboarding completion. The 4 KPI columns mirror the closed
// `AN_024_POV_KPI_FIELDS` vocabulary in `an-024-pov-baseline-events.ts`
// VERBATIM — drift between the event payload and these columns would silently
// drop fields at materialisation. Adding a new KPI ships a new column here
// AND a new entry in the catalog in the same commit.

export const tenantPovBaselines = pgTable(
  "tenant_pov_baselines",
  {
    id: uuid("id").primaryKey().defaultRandom(),
    tenantId: uuid("tenant_id").notNull(),
    /**
     * `onboarding_sessions.id` that produced the baseline. Anchors the audit
     * trail to the originating flow. Not a FK at the Drizzle layer — the
     * column lives in tenant DBs that may not host onboarding_sessions on
     * Tier 2 (schema-per-tenant); enforcement is at the route boundary.
     */
    onboardingSessionId: uuid("onboarding_session_id").notNull(),
    /** ISO timestamp the baseline was captured (onboarding completion). */
    capturedAt: timestamp("captured_at", tz).notNull(),
    /** Initial leads count — `AN_024_POV_KPI_FIELDS[0]`. */
    initialLeads: integer("initial_leads").notNull(),
    /**
     * Initial pipeline value in integer cents — `AN_024_POV_KPI_FIELDS[1]`.
     * `bigint` (number mode) because pipeline value can exceed 32-bit cents
     * for any mid-market tenant (~$21M cap on a plain int).
     */
    initialPipelineValueCents: bigint("initial_pipeline_value_cents", {
      mode: "number",
    }).notNull(),
    /** Initial active automations count — `AN_024_POV_KPI_FIELDS[2]`. */
    initialActiveAutomations: integer("initial_active_automations").notNull(),
    /** Initial team size count — `AN_024_POV_KPI_FIELDS[3]`. */
    initialTeamSize: integer("initial_team_size").notNull(),
    createdAt: timestamp("created_at", tz).notNull().defaultNow(),
    updatedAt: timestamp("updated_at", tz).notNull().defaultNow(),
  },
  (table) => [
    // Singleton per tenant — first-seen immutability enforced at the DB.
    uniqueIndex("idx_tenant_pov_baselines_tenant_unique").on(table.tenantId),
  ],
);

// ---------------------------------------------------------------------------
// 2. tenant_pov_snapshots — append-only weekly history
// ---------------------------------------------------------------------------
// Written by the weekly snapshot cron. UNIQUE on `(tenant_id, week_start_at)`
// makes the cron idempotent — a re-run for the same ISO week is a no-op.
// The POV report joins this table to `tenant_pov_baselines` on `tenant_id`
// and renders a per-week delta vs. baseline.

export const tenantPovSnapshots = pgTable(
  "tenant_pov_snapshots",
  {
    id: uuid("id").primaryKey().defaultRandom(),
    tenantId: uuid("tenant_id").notNull(),
    /** Wall-clock time the cron ran and read the values. */
    snapshotAt: timestamp("snapshot_at", tz).notNull(),
    /**
     * Monday 00:00 UTC of the week this snapshot represents. Pinned by the
     * cron so the POV generator groups by week without re-deriving from
     * `snapshotAt` (DST + tenant-timezone drift safe).
     */
    weekStartAt: timestamp("week_start_at", tz).notNull(),
    /** Current leads count at snapshot time. */
    initialLeads: integer("initial_leads").notNull(),
    /** Current pipeline value in integer cents at snapshot time. */
    initialPipelineValueCents: bigint("initial_pipeline_value_cents", {
      mode: "number",
    }).notNull(),
    /** Current active automations count at snapshot time. */
    initialActiveAutomations: integer("initial_active_automations").notNull(),
    /** Current team size count at snapshot time. */
    initialTeamSize: integer("initial_team_size").notNull(),
    createdAt: timestamp("created_at", tz).notNull().defaultNow(),
  },
  (table) => [
    // Idempotency: one snapshot per tenant per ISO week.
    uniqueIndex("idx_tenant_pov_snapshots_tenant_week_unique").on(
      table.tenantId,
      table.weekStartAt,
    ),
    // Time-series read path: per-tenant chronological scan.
    index("idx_tenant_pov_snapshots_tenant_snapshot_at").on(
      table.tenantId,
      table.snapshotAt,
    ),
  ],
);
